import { takeEvery, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import {
  amlCheckActions,
  amlHistoryActions,
  checkBatchActions,
  investigationHistoryActions,
  paymentActions,
  profileActions,
  promoHistoryActions,
  serviceOptionsActions,
  twoFAActions,
  userActions,
  addressMonitoringActions,
  webSocketActions,
  complianceStatsActions,
  complianceCategoryStatsActions,
  complianceTransferActions,
  complianceAlertStatsActions,
  complianceAlertsActions,
  complianceCustomersActions,
  complianceAlertLevelsActions,
  complianceListsActions,
  riskscoreProfilesActions,
  alertRulesActions,
  amlTraceActions,
  amlTraceHistoryActions,
} from '../actions';
import {
  amlCheckWorkers,
  amlHistoryWorkers,
  checkBatchWorkers,
  investigationHistoryWorkers,
  paymentWorkers,
  profileWorkers,
  promoHistoryWorkers,
  serviceOptionsWorkers,
  twoFAWorkers,
  userWorkers,
  addressMonitoringWorkers,
  webSocketWorkers,
  complianceStatsWorkers,
  complianceTransferWorkers,
  complianceAlertsWorkers,
  complianceCustomersWorkers,
  complianceAlertLevelsWorkers,
  complianceListsWorkers,
  riskscoreProfilesWorkers,
  alertRulesWorkers,
  amlTraceWorkers,
} from './workers';

function* rootSaga(): SagaIterator {
  yield takeEvery(userActions.check.type, userWorkers.check);
  yield takeEvery(userActions.current.type, userWorkers.current);
  yield takeEvery(userActions.signup.type, userWorkers.signup);
  yield takeEvery(userActions.signin.type, userWorkers.signin);
  yield takeEvery(userActions.googleAuth.type, userWorkers.googleAuth);
  yield takeEvery(userActions.telegramAuth.type, userWorkers.telegramAuth);
  yield takeEvery(userActions.signout.type, userWorkers.signout);
  yield takeEvery(userActions.confirmEmail.type, userWorkers.confirmEmail);
  yield takeEvery(
    userActions.forgotPasswordEmail.type,
    userWorkers.forgotPasswordEmail,
  );
  yield takeEvery(
    userActions.verifyRecoveryToken.type,
    userWorkers.verifyRecoveryToken,
  );
  yield takeEvery(userActions.resetPassword.type, userWorkers.resetPassword);
  yield takeEvery(
    userActions.changeFullNameEmail.type,
    userWorkers.changeFullNameEmail,
  );
  yield takeEvery(userActions.updateLocale.type, userWorkers.updateLocale);
  yield takeEvery(
    userActions.changeUserPassword.type,
    userWorkers.changeUserPassword,
  );
  yield takeEvery(
    userActions.updateCheckMode.type,
    userWorkers.updateCheckMode,
  );
  yield takeEvery(userActions.confirmTerms.type, userWorkers.confirmTerms);
  yield takeEvery(
    userActions.changeReceiveReminders.type,
    userWorkers.changeReceiveReminders,
  );
  yield takeEvery(
    userActions.discardAmlDetailsFor.type,
    userWorkers.discardAmlDetailsFor,
  );

  yield takeLatest(
    amlHistoryActions.fetchAmlHistory.type,
    amlHistoryWorkers.fetchAmlHistory,
  );
  yield takeLatest(
    amlHistoryActions.fetchHistoryItem.type,
    amlHistoryWorkers.fetchHistoryItem,
  );

  // 2FA
  yield takeEvery(twoFAActions.getTwoFACode.type, twoFAWorkers.getTwoFACode);
  yield takeEvery(twoFAActions.enableTwoFA.type, twoFAWorkers.enableTwoFA);
  yield takeEvery(twoFAActions.disableTwoFA.type, twoFAWorkers.disableTwoFA);

  yield takeLatest(
    profileActions.getProfileInfo.type,
    profileWorkers.getProfileInfo,
  );
  yield takeEvery(profileActions.monitorUser.type, profileWorkers.monitorUser);
  yield takeEvery(
    profileActions.updateAlertPeriod.type,
    profileWorkers.updateAlertPeriod,
  );
  yield takeEvery(
    profileActions.deleteBalanceAlert.type,
    profileWorkers.deleteBalanceAlert,
  );
  yield takeLatest(
    promoHistoryActions.fetchPromoHistory.type,
    promoHistoryWorkers.fetchPromoHistory,
  );
  yield takeEvery(
    promoHistoryActions.issuePromoCode.type,
    promoHistoryWorkers.issuePromoCode,
  );
  yield takeLatest(
    investigationHistoryActions.fetchInvestigationHistory.type,
    investigationHistoryWorkers.fetchInvestigationHistory,
  );
  yield takeLatest(
    investigationHistoryActions.exploreConnection.type,
    investigationHistoryWorkers.exploreConnection,
  );
  yield takeEvery(
    profileActions.fetchRefLinks.type,
    profileWorkers.fetchRefLinks,
  );
  yield takeEvery(
    profileActions.updateReflink.type,
    profileWorkers.updateReflink,
  );
  yield takeEvery(
    profileActions.createReflink.type,
    profileWorkers.createReflink,
  );
  yield takeEvery(
    profileActions.updatePreferences.type,
    profileWorkers.updatePreferences,
  );
  yield takeLatest(profileActions.pickUsage.type, profileWorkers.pickUsage);

  yield takeEvery(
    serviceOptionsActions.fetchOptions.type,
    serviceOptionsWorkers.fetchOptions,
  );
  yield takeEvery(
    paymentActions.fetchPriceListPlans.type,
    paymentWorkers.fetchPriceListPlans,
  );
  yield takeEvery(
    paymentActions.fetchInvoiceHistory.type,
    paymentWorkers.fetchInvoiceHistory,
  );
  yield takeEvery(
    paymentActions.enterPromoCode.type,
    paymentWorkers.enterPromoCode,
  );
  yield takeEvery(
    paymentActions.createStripeInvoice.type,
    paymentWorkers.createStripeInvoice,
  );
  yield takeEvery(
    paymentActions.createCryptoInvoice.type,
    paymentWorkers.createCryptoInvoice,
  );

  yield takeLatest(amlCheckActions.checkAml.type, amlCheckWorkers.checkAml);
  yield takeLatest(
    amlCheckActions.investigate.type,
    amlCheckWorkers.investigate,
  );
  yield takeEvery(amlCheckActions.checkAgain.type, amlCheckWorkers.checkAgain);
  yield takeLatest(
    checkBatchActions.fetchBatchHistory.type,
    checkBatchWorkers.fetchCheckBatchHistory,
  );
  yield takeEvery(
    checkBatchActions.fetchBatchItem.type,
    checkBatchWorkers.refreshBatchItem,
  );
  yield takeLatest(
    addressMonitoringActions.fetchMonitoringList.type,
    addressMonitoringWorkers.fetchMonitoringList,
  );
  yield takeEvery(
    addressMonitoringActions.unsubscribe.type,
    addressMonitoringWorkers.monitoringUnsubscribe,
  );
  yield takeLatest(webSocketActions.connect.type, webSocketWorkers.initiate);
  yield takeEvery(
    complianceStatsActions.fetchCustomerStats.type,
    complianceStatsWorkers.fetchCustomerStats,
  );
  yield takeEvery(
    complianceCategoryStatsActions.fetchCategoryStats.type,
    complianceStatsWorkers.fetchStatsByCategory,
  );
  yield takeEvery(
    complianceTransferActions.fetchTransferStats.type,
    complianceTransferWorkers.fetchTransferStats,
  );
  yield takeEvery(
    complianceTransferActions.fetchTransfers.type,
    complianceTransferWorkers.fetchTransfers,
  );
  yield takeEvery(
    complianceTransferActions.txRecheck.type,
    complianceTransferWorkers.txRecheck,
  );
  yield takeEvery(
    complianceTransferActions.applyDefault.type,
    complianceTransferWorkers.applyDefault,
  );
  yield takeEvery(
    complianceAlertStatsActions.fetchAlertStats.type,
    complianceAlertsWorkers.fetchAlertStats,
  );
  yield takeEvery(
    complianceAlertStatsActions.fetchAlertList.type,
    complianceAlertsWorkers.fetchStatsAlertList,
  );
  yield takeEvery(
    complianceAlertsActions.listAlerts.type,
    complianceAlertsWorkers.listAlerts,
  );
  yield takeEvery(
    complianceAlertsActions.updateStatus.type,
    complianceAlertsWorkers.updateStatus,
  );
  yield takeEvery(
    complianceCustomersActions.listCustomers.type,
    complianceCustomersWorkers.listCustomers,
  );
  yield takeEvery(
    complianceCustomersActions.updateCustomer.type,
    complianceCustomersWorkers.updateCustomer,
  );
  yield takeEvery(
    complianceAlertLevelsActions.listAlertLevels.type,
    complianceAlertLevelsWorkers.listAlertLevels,
  );
  yield takeEvery(
    complianceAlertLevelsActions.updateSettings.type,
    complianceAlertLevelsWorkers.updateSettings,
  );

  yield takeLatest(
    riskscoreProfilesActions.listRiskProfiles.type,
    riskscoreProfilesWorkers.listRiskProfiles,
  );

  yield takeLatest(
    riskscoreProfilesActions.createRiskProfile.type,
    riskscoreProfilesWorkers.createRiskProfile,
  );

  yield takeLatest(
    riskscoreProfilesActions.activateRiskProfile.type,
    riskscoreProfilesWorkers.activateRiskProfile,
  );

  yield takeEvery(
    riskscoreProfilesActions.setProfileSignals.type,
    riskscoreProfilesWorkers.setProfileSignals,
  );

  yield takeLatest(
    riskscoreProfilesActions.fetchRiskProfile.type,
    riskscoreProfilesWorkers.fetchRiskProfile,
  );

  yield takeLatest(
    riskscoreProfilesActions.resetRiskProfile.type,
    riskscoreProfilesWorkers.resetRiskProfile,
  );

  yield takeEvery(
    riskscoreProfilesActions.deleteRiskProfile.type,
    riskscoreProfilesWorkers.deleteRiskProfile,
  );

  yield takeEvery(
    userActions.googleAuthOneTap.type,
    userWorkers.googleAuthOneTap,
  );

  yield takeEvery(
    complianceAlertLevelsActions.resetTransactionAlertsCategories.type,
    complianceAlertLevelsWorkers.resetTransactionAlertsCategories,
  );

  yield takeEvery(
    complianceListsActions.fetchLists.type,
    complianceListsWorkers.fetchLists,
  );

  yield takeEvery(
    alertRulesActions.createAlertRule.type,
    alertRulesWorkers.createAlertRule,
  );

  yield takeLatest(
    alertRulesActions.listAlertRules.type,
    alertRulesWorkers.listAlertRules,
  );

  yield takeEvery(
    alertRulesActions.updateAlertRule.type,
    alertRulesWorkers.updateAlertRule,
  );

  yield takeEvery(
    alertRulesActions.deleteAlertRule.type,
    alertRulesWorkers.deleteAlertRule,
  );

  yield takeEvery(
    alertRulesActions.activateAlertRule.type,
    alertRulesWorkers.activateAlertRule,
  );

  yield takeEvery(
    alertRulesActions.deactivateAlertRule.type,
    alertRulesWorkers.deactivateAlertRule,
  );

  yield takeEvery(amlTraceActions.startTrace.type, amlTraceWorkers.startTrace);

  yield takeLatest(
    amlTraceHistoryActions.fetchTraceHistory.type,
    amlTraceWorkers.fetchTraceHistory,
  );

  yield takeLatest(
    amlTraceActions.fetchCurrencies.type,
    amlTraceWorkers.fetchCurrencies,
  );
}

export default rootSaga;
